/**
 * Walk group.fields for pathParts (nested groups in the middle); same rules as Filters.resolveNestedGroupField.
 * @returns {{ fieldDef: object|null, labelParts: string[] }}
 */
export function resolveNestedGroupFieldDef(groupField, pathParts) {
  if (!pathParts || pathParts.length === 0 || !groupField.fields) {
    return { fieldDef: null, labelParts: [] };
  }
  let currentFields = groupField.fields;
  const labelParts = [];
  for (let i = 0; i < pathParts.length; i++) {
    const part = pathParts[i];
    const isLast = i === pathParts.length - 1;
    const sub = currentFields.find((f) => f.key === part);
    if (!sub) return { fieldDef: null, labelParts: [] };
    const label = sub.label || sub.key || part;
    labelParts.push(label);
    if (isLast) {
      return { fieldDef: sub, labelParts };
    }
    if (sub.type !== "group" || !sub.fields) {
      return { fieldDef: null, labelParts: [] };
    }
    currentFields = sub.fields;
  }
  return { fieldDef: null, labelParts: [] };
}

/**
 * Subcampos de grupo en el modelo raíz (ej. shipping → expenses.type con grupo expenses isArray).
 * @param {object[]} modelFields
 * @param {string} filterKey — ej. "expenses.type"
 * @returns {object|null} { key, groupRootField, fieldDef, labelParts }
 */
export function resolveGroupSubfieldFilterField(modelFields, filterKey) {
  if (!filterKey || typeof filterKey !== "string" || !filterKey.includes(".")) {
    return null;
  }
  const groups = (modelFields || []).filter((f) => f && f.type === "group");
  let rootGroup = null;
  for (const g of groups) {
    if (filterKey.startsWith(g.key + ".")) {
      if (!rootGroup || g.key.length > rootGroup.key.length) {
        rootGroup = g;
      }
    }
  }
  if (!rootGroup) return null;
  const suffix = filterKey.slice(rootGroup.key.length + 1);
  if (!suffix) return null;
  const pathParts = suffix.split(".");
  const { fieldDef, labelParts } = resolveNestedGroupFieldDef(
    rootGroup,
    pathParts
  );
  if (!fieldDef || fieldDef.type === "group") return null;
  return {
    key: filterKey,
    groupRootField: rootGroup,
    fieldDef,
    labelParts,
  };
}

/**
 * Resolves a dotted path under a top-level reference field (single or isArray), including
 * one extra hop when the first segment on the referenced model is a reference with isArray
 * (e.g. items.lots.trackingCode -> items ref, lots array ref on target, trackingCode on nested model).
 *
 * @param {object[]} modelFields - Model definition fields (may include groups; only top-level references matched).
 * @param {string} filterKey - Full key e.g. "lineItems.product.name"
 * @param {(name: string) => Promise<object|null>} getModelDefinition
 * @returns {Promise<object|null>} Virtual reference_subfield field descriptor, or null if not a reference path.
 */
export async function resolveReferenceSubfieldFilterField(
  modelFields,
  filterKey,
  getModelDefinition
) {
  if (!filterKey || typeof filterKey !== "string" || !filterKey.includes(".")) {
    return null;
  }

  const nonGroup = (modelFields || []).filter((f) => f && f.type !== "group");
  let parentRef = null;
  for (const f of nonGroup) {
    if (
      f.type === "reference" &&
      f.ref &&
      filterKey.startsWith(f.key + ".")
    ) {
      if (!parentRef || f.key.length > parentRef.key.length) {
        parentRef = f;
      }
    }
  }
  if (!parentRef) return null;

  const subFieldPath = filterKey.slice(parentRef.key.length + 1);
  if (!subFieldPath) return null;

  const refModelDef = await getModelDefinition(parentRef.ref);
  if (!refModelDef?.fields?.length) return null;

  const pathParts = subFieldPath.split(".");
  const firstSubFieldKey = pathParts[0];
  const subFieldDef =
    refModelDef.fields.find((ff) => ff.key === firstSubFieldKey) || {};

  let nestedFieldKey = null;
  let nestedFieldDef = null;

  if (pathParts.length > 1) {
    nestedFieldKey = pathParts.slice(1).join(".");
    if (
      subFieldDef.type === "reference" &&
      subFieldDef.ref &&
      subFieldDef.isArray
    ) {
      const nestedRefModelDef = await getModelDefinition(subFieldDef.ref);
      nestedFieldDef =
        nestedRefModelDef?.fields?.find((ff) => ff.key === nestedFieldKey) ||
        {};
    }
  }

  const leafDef =
    nestedFieldDef && Object.keys(nestedFieldDef).length
      ? nestedFieldDef
      : subFieldDef;

  return {
    key: filterKey,
    type: "reference_subfield",
    parentField: parentRef,
    subFieldKey: firstSubFieldKey,
    nestedFieldKey,
    subFieldType: leafDef.type,
    subFieldRef: leafDef.ref || null,
    subFieldListType: leafDef.listType || null,
    subFieldListRef: leafDef.listRef || null,
    subFieldDef: leafDef,
    nestedSubFieldDef:
      nestedFieldDef && Object.keys(nestedFieldDef).length
        ? nestedFieldDef
        : null,
    ref: parentRef.ref,
    isArray: !!subFieldDef.isArray,
    // Intermediate reference (array) on referenced doc, when we resolve a nested leaf
    intermediateRefField:
      pathParts.length > 1 &&
      subFieldDef.type === "reference" &&
      subFieldDef.isArray
        ? subFieldDef
        : null,
  };
}
